/**
 * TableConfigBlock — bloque "Configuración de la tabla" del Designer
 * (Doc 24 §3 Fase 10).
 *
 * Solo se monta cuando `section.type === 'structured_table'`. Edita el
 * shape v2 `TableConfig = { columns: TableColumn[] }` íntegro vía un
 * único callback `onChange`.
 *
 * UX:
 *   - Lista vertical de columnas, cada una colapsable
 *     (`TableColumnEditor`).
 *   - Una sola columna expandida a la vez para evitar paredes de UI.
 *   - Botón "Agregar columna" al final → nueva columna en modo edit
 *     con `key = col_N` auto-generado.
 *
 * Validación cruzada — detecta duplicados de `column.key` y los marca
 * en cada columna afectada (passing `keyDuplicate` prop al hijo).
 *
 * Cambio de tipo enum → otro: si la columna tiene `enum_values`, se
 * pide confirmación via `window.confirm` (UX MVP — Fase F migra a
 * Dialog del design system cuando llegue). El padre puede sobreescribir
 * vía `onConfirmDestructiveTypeChange` para tests.
 *
 * El componente es presentacional / controlled — no toca DesignerState.
 * El padre (`SectionEditorPane`) wirea `onChange` al dispatch
 * `UPDATE_STRUCTURAL { patch: { table_config: next } }`.
 */

import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { TableColumn, TableConfig } from '../../../api/templates'

import { TableColumnEditor } from './TableColumnEditor'
import type { SupportedLocale } from '../../../i18n/config'

export interface TableConfigBlockProps {
  locale: SupportedLocale
  /** Locales activos del documento (`Object.keys(definition.i18n)`).
   * El editor de columnas y enum_values se restringe a estos — si solo
   * está `["es"]`, no se renderea la tab/columna EN. */
  activeLocales: string[]
  /** Puede llegar undefined si la sección recién se cambió a
   * structured_table — se trata como `{ columns: [] }`. */
  value?: TableConfig | null | undefined
  readOnly?: boolean
  onChange(next: TableConfig): void
  /** Override para tests. Default = window.confirm. */
  confirmDestructive?: (message: string) => boolean
}

function generateColumnKey(existing: TableColumn[]): string {
  const taken = new Set(existing.map((c) => c.key))
  let n = existing.length + 1
  while (taken.has(`col_${n}`)) n += 1
  return `col_${n}`
}

function makeNewColumn(
  existing: TableColumn[],
  activeLocales: string[],
): TableColumn {
  const i18n: Record<string, { label: string }> = {}
  for (const loc of activeLocales) i18n[loc] = { label: '' }
  return {
    key: generateColumnKey(existing),
    type: 'plain_text',
    required: false,
    i18n,
  }
}

export function TableConfigBlock({
  locale,
  activeLocales,
  value,
  readOnly = false,
  onChange,
  confirmDestructive,
}: TableConfigBlockProps) {
  const { t } = useTranslation('templates')
  const columns = value?.columns ?? []

  // Tracking de qué columna está expandida (mutuamente exclusivo).
  // Usamos `idx` (no `key`) porque al editar la key el matching por key
  // se rompe entre renders.
  const [expandedIdx, setExpandedIdx] = useState<number | null>(
    columns.length === 0 ? null : null,
  )

  // Pre-cálculo de duplicados.
  const keyCounts = new Map<string, number>()
  for (const c of columns) {
    keyCounts.set(c.key, (keyCounts.get(c.key) ?? 0) + 1)
  }

  function emit(nextColumns: TableColumn[]) {
    onChange({ columns: nextColumns })
  }

  function updateAt(idx: number, next: TableColumn) {
    const nextCols = columns.map((c, i) => (i === idx ? next : c))
    emit(nextCols)
  }

  function removeAt(idx: number) {
    emit(columns.filter((_, i) => i !== idx))
    if (expandedIdx === idx) setExpandedIdx(null)
  }

  function addColumn() {
    const newCol = makeNewColumn(columns, activeLocales)
    emit([...columns, newCol])
    // Auto-expandir la nueva.
    setExpandedIdx(columns.length)
  }

  function toggleExpand(idx: number) {
    setExpandedIdx((curr) => (curr === idx ? null : idx))
  }

  function handleBeforeTypeChange(
    from: TableColumn['type'],
    to: TableColumn['type'],
    hasEnumValues: boolean,
  ): boolean {
    if (from === 'enum' && to !== 'enum' && hasEnumValues) {
      const message = t(
        'designer.editor.table_config_v2.confirm_lose_enum_values',
      ) as string
      const confirm = confirmDestructive ?? window.confirm
      return confirm(message)
    }
    return true
  }

  return (
    <div
      className="rounded-md border border-line bg-surface-muted/40 p-3"
      data-testid="table-config-block"
    >
      <p className="text-xs font-medium text-ink-muted mb-1">
        {t('designer.editor.table_config_v2.block_title')}
      </p>
      <p className="text-[11px] text-ink-subtle italic mb-2">
        {t('designer.editor.table_config_v2.columns_label')}
      </p>

      {columns.length === 0 ? (
        <p
          className="text-[11px] text-ink-subtle italic mb-2"
          data-testid="table-config-empty"
        >
          {t('designer.editor.table_config_v2.no_columns_yet')}
        </p>
      ) : (
        <ul
          className="space-y-1.5 mb-2"
          data-testid="table-config-columns-list"
        >
          {columns.map((col, idx) => (
            <TableColumnEditor
              key={idx}
              column={col}
              keyDuplicate={(keyCounts.get(col.key) ?? 0) > 1}
              locale={locale}
              activeLocales={activeLocales}
              expanded={expandedIdx === idx}
              readOnly={readOnly}
              onChange={(next) => updateAt(idx, next)}
              onRemove={() => removeAt(idx)}
              onToggleExpand={() => toggleExpand(idx)}
              onBeforeTypeChange={handleBeforeTypeChange}
              testId={`table-config-column-${idx}`}
            />
          ))}
        </ul>
      )}

      {!readOnly ? (
        <button
          type="button"
          onClick={addColumn}
          data-testid="table-config-add-column"
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-line text-ink hover:bg-surface-muted"
        >
          <Plus className="w-3.5 h-3.5" />
          {t('designer.editor.table_config_v2.add_column')}
        </button>
      ) : null}
    </div>
  )
}
